import { PEOPLE_SCREEN_PATH } from "../../../utils/constants/routes";

// Prospects Tour (key: "prospects")
// Sub-tour 1 of "Prospects + Marketing List" group.
//
// 6 steps walking the user through the People section:
//   1. Spotlight the Prospects sidebar nav item
//   2. Programmatically open the submenu, spotlight the People sub-tab
//   3. Navigate to /user/people (no filters), spotlight the filters bar
//   4. Open Seniority filter + select Owner programmatically, spotlight the open dropdown
//   5. Close dropdown, ensure Owner is selected + table loaded, spotlight the table
//   6. Spotlight the Save View button
//
// Resume is fully supported - each step's before() sets up all required state
// independently so the tour is self-contained from any step.

export function getProspectsTourSteps(navigate, store) {
  // Clicks all visible MUI modal backdrops to close any open menus/submenus.
  // More reliable than dispatching keyboard events programmatically.
  const closeOpenMenus = async () => {
    document.querySelectorAll(".MuiBackdrop-root").forEach((el) => el.click());
    await new Promise((resolve) => setTimeout(resolve, 250));
  };

  // Navigates to /user/people with no query params (clears any active filters in URL).
  // Skipped if already on the clean URL (pathname matches, no search).
  const goToCleanPeople = (delay = 800) => {
    const isClean =
      window.location.pathname === PEOPLE_SCREEN_PATH && !window.location.search;
    if (isClean) return Promise.resolve();
    navigate(PEOPLE_SCREEN_PATH);
    return new Promise((resolve) => setTimeout(resolve, delay));
  };

  // Strips stray non-filter params (e.g. tab=LearningCenter leaked from Redux
  // initialState when the store was seeded on the settings page) from both the
  // current URL AND the Redux allFilters state.
  //
  // Cleaning the URL alone is not enough: allFilters.payload is seeded from the
  // URL at app startup, so it permanently holds tab=LearningCenter even after
  // navigation. Every subsequent filter handleChange spreads allFiltersSelector
  // back into navigate(), re-injecting tab. Both must be cleaned together.
  const cleanTabParam = async () => {
    const params = new URLSearchParams(window.location.search);
    const urlHasTab = params.has("tab");
    const urlHasTenantPath = params.has("tenant_path");
    const reduxPayload = store?.getState()?.allFilters?.payload;
    const reduxHasTab = !!(reduxPayload?.tab);
    const reduxHasTenantPath = !!(reduxPayload?.tenant_path);

    if (!urlHasTab && !urlHasTenantPath && !reduxHasTab && !reduxHasTenantPath) return;

    if (urlHasTab || urlHasTenantPath) {
      params.delete("tab");
      params.delete("tenant_path");
      navigate(
        { pathname: window.location.pathname, search: params.toString() },
        { replace: true },
      );
    }

    if (reduxHasTab || reduxHasTenantPath) {
      const cleaned = { ...reduxPayload };
      delete cleaned.tab;
      delete cleaned.tenant_path;
      store.dispatch({ type: "ALL_FILTERS_SUCCESS", payload: cleaned });
    }

    await new Promise((resolve) => setTimeout(resolve, 150));
  };

  // Runs fn() while keeping the seniority button visually frozen at its current
  // appearance and keeping the MUI dropdown/backdrop invisible.
  //
  // Why snapshot instead of just hiding: the button gets aria-expanded="true" when
  // the dropdown opens, which triggers the app's brand-color CSS. Reading the
  // computed style beforehand and locking those exact values for every interactive
  // state (hover / focus / active / aria-expanded) keeps the button looking normal
  // without hiding it. MUI's ripple is also suppressed so there is no click flash.
  // The injected <style> is removed in a finally block so it never leaks outside
  // of tour transitions.
  const withHiddenDropdown = async (fn) => {
    const btn = document.querySelector('[data-tour="seniority-filter-btn"]');

    let btnFreeze = "";
    if (btn) {
      const cs = window.getComputedStyle(btn);
      const bg = cs.backgroundColor;
      const fg = cs.color;
      const bc = cs.borderTopColor;
      const fw = cs.fontWeight;
      const fs = cs.fontSize;
      const sel = "[data-tour='seniority-filter-btn']";

      // Snapshot every child element's background-color so none of them shift
      // when aria-expanded="true" triggers descendant CSS rules (e.g. the badge).
      const childSnapshots = [];
      btn.querySelectorAll("*").forEach((child, i) => {
        const childCs = window.getComputedStyle(child);
        const childBg = childCs.backgroundColor;
        const childFg = childCs.color;
        const childFw = childCs.fontWeight;
        const childFs = childCs.fontSize;
        if (!child.dataset.tourFreezeId) child.dataset.tourFreezeId = `tf${i}`;
        const id = child.dataset.tourFreezeId;
        childSnapshots.push(
          `${sel} [data-tour-freeze-id="${id}"]{` +
          `background-color:${childBg}!important;color:${childFg}!important;` +
          `font-weight:${childFw}!important;font-size:${childFs}!important;` +
          `transition:none!important}`
        );
      });

      btnFreeze =
        `${sel},${sel}:hover,${sel}:focus,${sel}:focus-visible,` +
        `${sel}:active,${sel}[aria-expanded="true"]` +
        `{background-color:${bg}!important;color:${fg}!important;` +
        `border-color:${bc}!important;font-weight:${fw}!important;` +
        `font-size:${fs}!important;outline:none!important;` +
        `box-shadow:none!important;transition:none!important}` +
        `${sel} .MuiTouchRipple-root{display:none!important}` +
        childSnapshots.join("");
    }

    const style = document.createElement("style");
    style.textContent =
      ".MuiPopover-root,.MuiBackdrop-root{opacity:0!important;transition:none!important}" +
      btnFreeze;
    document.head.appendChild(style);
    try {
      await fn();
    } finally {
      document.head.removeChild(style);
      if (btn) {
        btn.querySelectorAll("[data-tour-freeze-id]").forEach((el) => {
          delete el.dataset.tourFreezeId;
        });
      }
    }
  };

  // Clears the Owner seniority filter if it is currently active.
  // Must go through the dropdown click path (not URL navigation) so that
  // handleSeniorityChange updates Redux + retriggers the API without the filter.
  // Skipped entirely when the URL has no seniority param (filter already clear).
  // The dropdown is opened and closed invisibly via withHiddenDropdown.
  const clearOwnerFilter = async () => {
    if (!window.location.search.includes("seniority=owner")) return;
    if (window.location.pathname !== PEOPLE_SCREEN_PATH) return;
    const seniorityBtn = document.querySelector('[data-tour="seniority-filter-btn"]');
    if (!seniorityBtn) return;
    await withHiddenDropdown(async () => {
      seniorityBtn.click();
      seniorityBtn.blur();
      await new Promise((resolve) => setTimeout(resolve, 400));
      const ownerCheckbox = document.querySelector(
        '[data-tour="seniority-owner-option"] input[type="checkbox"]'
      );
      if (ownerCheckbox && ownerCheckbox.checked) {
        document.querySelector('[data-tour="seniority-owner-option"]').click();
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
      document.querySelectorAll(".MuiBackdrop-root").forEach((el) => el.click());
      await new Promise((resolve) => setTimeout(resolve, 250));
    });
  };

  // Polls the DOM for selector until it appears or timeout (ms) elapses.
  // Avoids fixed-duration sleeps after navigate() — returns as soon as the
  // element is ready (or null on timeout).
  const waitForElement = async (selector, timeout = 3000) => {
    const deadline = Date.now() + timeout;
    while (Date.now() < deadline) {
      const el = document.querySelector(selector);
      if (el) return el;
      await new Promise((r) => setTimeout(r, 100));
    }
    return null;
  };

  // Ensures the People page is open with Owner selected and the table loaded.
  //
  // URL fast path: if seniority=owner is already in the URL we skip the dropdown
  // entirely — but still strip any stray tab param before returning.
  // Full path: navigates if needed, waits for the filter button to appear (up to
  // 3 s — more robust than a fixed 800 ms sleep when resuming from settings),
  // opens the dropdown invisibly, clicks Owner if not already checked, waits for
  // the API, closes, then strips any stray tab param.
  // cleanTabParam() is called on ALL exit paths so tab=LearningCenter can never survive.
  const ensureOwnerSelected = async (fullDelay = 2000, shortDelay = 500) => {
    // Fast path — seniority=owner already active in URL
    if (
      window.location.pathname === PEOPLE_SCREEN_PATH &&
      new URLSearchParams(window.location.search).get("seniority") === "owner"
    ) {
      await cleanTabParam();
      return;
    }

    // Clean Redux BEFORE any navigation or filter click. handleSeniorityChange
    // reads allFiltersSelector at click-time; if Redux is already clean the API
    // payload will never contain tab or tenant_path in the first place.
    await cleanTabParam();

    if (window.location.pathname !== PEOPLE_SCREEN_PATH) {
      navigate(PEOPLE_SCREEN_PATH);
    }
    // Poll for the button instead of a fixed sleep — handles slow page renders
    // (especially when resuming from the settings page where 800 ms wasn't enough).
    const seniorityBtn = await waitForElement('[data-tour="seniority-filter-btn"]', 3000);
    if (!seniorityBtn) return;
    await withHiddenDropdown(async () => {
      seniorityBtn.click();
      seniorityBtn.blur();
      await new Promise((resolve) => setTimeout(resolve, 400));
      const ownerCheckbox = document.querySelector(
        '[data-tour="seniority-owner-option"] input[type="checkbox"]'
      );
      if (ownerCheckbox && !ownerCheckbox.checked) {
        document.querySelector('[data-tour="seniority-owner-option"]').click();
        await new Promise((resolve) => setTimeout(resolve, fullDelay));
      } else {
        await new Promise((resolve) => setTimeout(resolve, shortDelay));
      }
      document.querySelectorAll(".MuiBackdrop-root").forEach((el) => el.click());
      await new Promise((resolve) => setTimeout(resolve, 250));
    });

    // Final defensive strip in case something re-injected during the interaction
    await cleanTabParam();
  };

  return [
    // Step 1: Prospects sidebar nav item.
    // before() closes any lingering submenus (e.g. if user went back from step 2).
    {
      target: '[data-tour="prospects-nav"]',
      placement: "right",
      title: "Where Your Outreach Journey Begins",
      content:
        "This is your starting point. The place where you find a massive pool of verified contacts, so you can start building your ideal outreach audience.",
      blockTargetInteraction: true,
      hideBackButton: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        // Clean tab from both URL and Redux immediately — allFilters.payload was
        // seeded with tab=LearningCenter at startup and must be purged before any
        // filter action fires, or handleChange will re-inject it via allFiltersSelector.
        await cleanTabParam();
        await closeOpenMenus();
      },
    },

    // Step 2: People sub-tab.
    // before() clicks Prospects nav to open the submenu.
    // Works for both "next from step 1" and "resume at step 2".
    {
      target: '[data-tour="people-sub-tab"]',
      placement: "right",
      title: "Meet the People Section",
      content:
        "This is where you discover contacts. Verified profiles at your fingertips, all searchable and ready for your outreach.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        const prospectsBtn = document.querySelector('[data-tour="prospects-nav"]');
        if (prospectsBtn) prospectsBtn.click();
        await new Promise((resolve) => setTimeout(resolve, 400));
      },
    },

    // Step 3: Filters bar.
    // Closes any open menus, clears Owner filter via the dropdown (so Redux + API
    // update properly and the badge disappears), then navigates to clean /user/people.
    {
      target: '[data-tour="filters-strip"]',
      placement: "bottom",
      title: "Your Filtering Toolkit",
      content:
        "These filters are your precision tools. Narrow down by seniority, company size, industry, and much more to find exactly the right people.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await closeOpenMenus();
        // Purge tab from Redux before clearOwnerFilter runs — if seniority=owner
        // is active, clearOwnerFilter will click the dropdown, which spreads
        // allFiltersSelector into navigate(). Cleaning Redux first prevents tab
        // from re-appearing in the URL during that click.
        await cleanTabParam();
        await clearOwnerFilter();
        await goToCleanPeople();
      },
    },

    // Step 4: Seniority filter dropdown (open, Owner checked).
    // Ensures we are on the People page, opens the seniority filter, then clicks Owner
    // only if it is not already checked (prevents toggling it off on back-navigation).
    // The dropdown stays open after selection (multi-select, no auto-close on check).
    {
      target: '[data-tour="seniority-filter-menu"]',
      placement: "right",
      title: "Filters in Action",
      content: "Let's say you want to reach Owners. We have set it for you. Combine filters like this to find exactly the audience you want.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      targetWaitTimeout: 2000,
      before: async () => {
        await closeOpenMenus();
        // Clean Redux BEFORE the click so handleSeniorityChange reads clean state
        // and the API payload never contains tab or tenant_path.
        await cleanTabParam();
        if (window.location.pathname !== PEOPLE_SCREEN_PATH) {
          navigate(PEOPLE_SCREEN_PATH);
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
        const seniorityBtn = document.querySelector('[data-tour="seniority-filter-btn"]');
        if (seniorityBtn) seniorityBtn.click();
        await new Promise((resolve) => setTimeout(resolve, 400));
        const ownerCheckbox = document.querySelector(
          '[data-tour="seniority-owner-option"] input[type="checkbox"]'
        );
        if (ownerCheckbox && !ownerCheckbox.checked) {
          document.querySelector('[data-tour="seniority-owner-option"]').click();
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
        // Final defensive strip
        await cleanTabParam();
      },
    },

    // Step 5: Results table.
    // Closes the open seniority dropdown (from step 4), then ensures Owner is selected
    // and the table has loaded. ensureOwnerSelected goes through the click path so
    // Redux + the API are always triggered correctly (handles resume case too).
    {
      target: '[data-tour="prospects-table"]',
      placement: "top",
      title: "There's Your Prospect Pool",
      content:
        "Just like that, you have a big pool of verified Owners right there. The exact audience you wanted to find.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      tooltipMaxWidth: 440,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(2000, 500);
      },
    },

    // Step 6: Save View button.
    // Closes any open menus, ensures Owner filter + table are active (prerequisite for
    // going back to step 5). blockTargetInteraction prevents clicking the button itself.
    {
      target: '[data-tour="save-view-btn"]',
      placement: "bottom",
      title: "Save Your Favorite Filter Sets",
      content:
        "When a filter combination feels right and you know you will keep using it, save it as a View. Next time you are back, apply it instantly without having to reconstruct it again and again.",
      blockTargetInteraction: true,
      spotlightPadding: 2,
      spotlightRadius: 6,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(1500, 0);
      },
    },
  ];
}
